import { Grid } from '@mui/material';
import styled from 'styled-components';
import { useAuth } from 'context/AuthContext';

import { useState } from 'react';

const Div = styled.div`
    display: flex;
    gap: 20px;
    margin-bottom: 16px;
    transition: all 0.3s ease;

    .text {
        font-family: Poppins, serif;
        font-size: 14px;
        font-style: normal;
        font-weight: 400;
        line-height: normal;
    }

    .imageContainer {
        border-radius: 8px;
        border: 1px solid #CBC1B7;
        background: ${(props) => props.theme.colorBackground};
        width: 100px;
        height: 100px;
        min-width: 100px;
        min-height: 100px;
        transition: all 0.3s ease;
        max-width: 100px;
        max-height: 100px;
        padding: 12px 20px 20px 20px;
        display: flex;
        justify-content: center;
        flex-direction: column;
        align-items: center;
        flex-shrink: 0;
        box-sizing: border-box;
        transition: border-color 0.3s ease, background-color 0.3s ease;
        cursor: pointer;
        gap: 6px;
        
        &:hover {
            border-color: #ff5f39;
            background-color: white;
        }
    }

    .imageText {
        color: ${(props) => props.theme.tertiaryFontColor};

        display: flex;
        flex-direction: column;
        justify-content: space-between;
    }

    .addPhotoText {
        color: #999;
        text-align: center;
        transition: color 0.3s ease;
        white-space: nowrap;
        line-height: 1;
    }
    
    .imageContainer:hover .addPhotoText {
        color: #ff5f39;
    }
    
    .imageContainer:hover svg path,
    .imageContainer:hover svg circle {
        stroke: #ff5f39 !important;
        transition: stroke 0.3s ease;
    }

    .imageWrapper {
        position: relative;
        width: 100px;
        height: 100px;
        min-width: 100px;
        min-height: 100px;
        flex-shrink: 0;
        cursor: pointer;
        
        &:hover .image {
            border: 1px solid #ff5f39;
            opacity: 0.2;
        }
        
        &:hover .imageOverlay {
            display: flex;
        }
        
        &:hover .imageOverlay .addPhotoText {
            color: #ff5f39;
        }
        
        &:hover .imageOverlay svg path,
        &:hover .imageOverlay svg circle {
            stroke: #ff5f39 !important;
            transition: stroke 0.3s ease;
        }
    }

    .image {
        width: 100px;
        height: 100px;
        border-radius: 8px;
        flex-shrink: 0;
        border: 1px solid var(--Structural-Structure-3, #c4c4c4);
        transition: border-color 0.3s ease, opacity 0.3s ease;
        display: block;
        object-fit: cover;
    }
    
    .imageOverlay {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        display: none;
        align-items: center;
        justify-content: center;
        flex-direction: column;
        gap: 6px;
        pointer-events: none;
        border-radius: 8px;
    }
    
`;
const ImageUploader = ({ formData, setFormData }) => {
    const [uploading, setUploading] = useState(false);
    // The base64 data URL is preview-only and deliberately kept out of formData. It
    // used to be written straight to image_url, so any upload that failed - or a
    // submit that beat the upload - sent the whole image inside the provider JSON,
    // which the backend rejects (413) as an opaque "Failed to fetch".
    const [preview, setPreview] = useState(null);
    const [uploadError, setUploadError] = useState('');
    const { token } = useAuth();

    const displayedImage = preview || formData.image_url;

    const openFilePicker = () => {
        if (uploading) return;
        document.getElementById('provider-image').click();
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        // Let the same file be picked again after a failed attempt.
        e.target.value = '';
        
        if (!file) return;

        // Validate token
        if (!token) {
            console.error('No authentication token found');
            setUploadError('Your session has expired. Please sign in again.');
            return;
        }

        // Set uploading state
        setUploading(true);
        setUploadError('');

        try {
            // Show the picked file straight away - as preview state only
            const reader = new FileReader();
            reader.onloadend = () => {
                setPreview(reader.result);
            };
            reader.readAsDataURL(file);

            // Create FormData for file upload
            const formDataToSend = new FormData();
            formDataToSend.append('file', file);

            // Upload to backend API (same endpoint as business image upload)
            const response = await fetch(
                `${process.env.REACT_APP_SERVER_URL}/api/providers/upload/business-image`,
                {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`
                        // Don't set Content-Type - let browser set it with boundary for FormData
                    },
                    body: formDataToSend
                }
            );

            const result = await response.json().catch(() => ({}));

            if (!response.ok || result.error) {
                const errorMessage = result.error || 'Failed to upload image';
                console.error('Image upload error:', errorMessage);
                setPreview(null);
                setUploadError(errorMessage);
                return;
            }

            // Backend should return the image URL in result.data or result.url
            const fileUrl = result.data?.url || result.data?.image_url || result.url || result.image_url;

            if (!fileUrl) {
                console.error('No image URL returned from server');
                setPreview(null);
                setUploadError('Upload failed: no image URL returned. Please try again.');
                return;
            }

            // Only the hosted URL is ever put on the form
            setFormData((prev) => ({
                ...prev,
                image_url: fileUrl,
            }));
            setPreview(null);
        } catch (error) {
            console.error('Image upload error:', error);
            setPreview(null);
            setUploadError('Image upload failed. Please check your connection and try again.');
        } finally {
            setUploading(false);
        }
    };

    return (
        <Grid item xs={12}>
            {/* Hidden file input for image upload */}
            <input type="file" id="provider-image" accept="image/*" onChange={handleImageUpload} className="hidden" />

            <Div>
                {displayedImage ? (
                    <div className="imageWrapper" onClick={openFilePicker}>
                        <img
                            src={displayedImage}
                            alt="Provider"
                            className="image"
                        />
                        <div className="imageOverlay">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                style={{
                                    minWidth: '24px',
                                    minHeight: '24px',
                                    maxWidth: '24px',
                                    maxHeight: '24px',
                                    flexShrink: 0
                                }}
                            >
                                <path
                                    d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"
                                    stroke="#999"
                                    strokeWidth="2"
                                    fill="none"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    style={{ transition: 'stroke 0.3s ease' }}
                                />
                                <circle
                                    cx="12"
                                    cy="13"
                                    r="4"
                                    stroke="#999"
                                    strokeWidth="2"
                                    fill="none"
                                    style={{ transition: 'stroke 0.3s ease' }}
                                />
                            </svg>
                            <span className="text addPhotoText" style={{ fontSize: '12px' }}>Replace</span>
                        </div>
                    </div>
                ) : (
                    <div className="imageContainer" onClick={openFilePicker}>
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            style={{
                                minWidth: '24px',
                                minHeight: '24px',
                                maxWidth: '24px',
                                maxHeight: '24px',
                                flexShrink: 0
                            }}
                        >
                            <path
                                d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"
                                stroke="#999"
                                strokeWidth="2"
                                fill="none"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                style={{ transition: 'stroke 0.3s ease' }}
                            />
                            <circle
                                cx="12"
                                cy="13"
                                r="4"
                                stroke="#999"
                                strokeWidth="2"
                                fill="none"
                                style={{ transition: 'stroke 0.3s ease' }}
                            />
                        </svg>
                        <span className="text addPhotoText" style={{ fontSize: '12px' }}>Add photo</span>
                    </div>
                )}

                <div className="imageText">
                    <div className="help-text">
                        Add a professional photo for your provider to display in search results and on your provider's
                        profile.
                    </div>
                    <div className="help-text">
                        {uploading ? 'Uploading image...' : 'Upload a 200x200 image, up to 4MB in size.'}
                    </div>
                </div>
            </Div>
            {uploadError && <div className={'text-red-500 mb-2'}>{uploadError}</div>}
        </Grid>
    );
};

export default ImageUploader;
